/**
 * PrescriptionTemplateModal - Modal para criar/editar templates de receitas
 * Sistema de Receitas Pré-Cadastradas
 */

import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Save, 
  Plus, 
  Trash2, 
  X,
  FileText,
  Pill,
  AlertTriangle
} from 'lucide-react';
import MedicationGroupByRoute, { createEmptyMedication, DOSAGE_UNITS } from './MedicationGroupByRoute';

export default function PrescriptionTemplateModal({ 
  open, 
  onClose, 
  template = null, // Se passado, é edição
  onSuccess 
}) {
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [diseaseTags, setDiseaseTags] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Medicamentos do template
  const [medicationGroups, setMedicationGroups] = useState([
    {
      via: 'ORAL',
      medicamentos: [createEmptyMedication()]
    }
  ]);

  // Preencher dados se for edição
  useEffect(() => {
    if (open && template) {
      setTemplateName(template.template_name || '');
      setDescription(template.description || '');
      setSpecialty(template.specialty || '');
      setDiseaseTags(template.disease_tags?.join(', ') || '');
      setIsFavorite(template.is_favorite || false);
      
      // Converter medicamentos do template para o formato de grupos
      if (template.medications && template.medications.length > 0) {
        const groups = {};
        for (const med of template.medications) {
          const via = (med.route || 'USO ORAL').replace('USO ', '');
          if (!groups[via]) {
            groups[via] = [];
          }
          groups[via].push({
            name: med.name || '',
            dosage: med.dosage || '',
            dosage_unit: med.dosage_unit || 'MG',
            instructions: med.instructions || med.frequency || '',
            quantity: med.quantity || '',
            packaging_type: med.packaging_type || '',
            dosage_type: med.dosage_type || 'quantidade'
          });
        }
        
        const groupsArray = Object.entries(groups).map(([via, meds]) => ({
          via,
          medicamentos: meds
        }));
        
        setMedicationGroups(groupsArray.length > 0 ? groupsArray : [{
          via: 'ORAL',
          medicamentos: [createEmptyMedication()]
        }]);
      }
    } else if (open && !template) {
      // Reset para novo template
      handleReset();
    }
  }, [open, template]);

  const handleReset = () => {
    setTemplateName('');
    setDescription('');
    setSpecialty('');
    setDiseaseTags('');
    setIsFavorite(false);
    setMedicationGroups([{
      via: 'ORAL',
      medicamentos: [createEmptyMedication()]
    }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!templateName.trim()) {
      toast.error('Informe o nome do template');
      return;
    }
    
    // Validar medicamentos
    const hasValidMedication = medicationGroups.some(group => 
      group.medicamentos.some(med => med.name?.trim())
    );
    
    if (!hasValidMedication) {
      toast.error('Adicione pelo menos um medicamento');
      return;
    }
    
    setLoading(true);
    try {
      // Converter grupos para lista de medicamentos
      const medications = [];
      for (const group of medicationGroups) {
        for (const med of group.medicamentos) {
          if (med.name?.trim()) {
            medications.push({
              name: med.name,
              dosage: med.dosage || '',
              dosage_unit: med.dosage_unit || 'MG',
              route: `USO ${group.via}`,
              frequency: med.instructions || '',
              duration: '',
              instructions: med.instructions || '',
              quantity: med.quantity || '',
              packaging_type: med.packaging_type || '',
              dosage_type: med.dosage_type || 'quantidade'
            });
          }
        }
      }
      
      // Parse disease tags
      const tags = diseaseTags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      const payload = {
        template_name: templateName,
        description: description || null,
        specialty: specialty || null,
        disease_tags: tags,
        medications: medications,
        is_favorite: isFavorite
      };
      
      if (template) {
        // Atualizar existente
        await axiosInstance.put(`/prescription-templates/${template.id}`, payload);
        toast.success('Template atualizado com sucesso!');
      } else {
        // Criar novo
        await axiosInstance.post('/prescription-templates/', payload);
        toast.success('Template criado com sucesso!');
      }
      
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-5 h-5 text-primary" />
            {template ? 'Editar Receita Modelo' : 'Nova Receita Modelo'}
          </DialogTitle>
          <DialogDescription>
            {template 
              ? 'Edite os dados do template de receita. Receitas já criadas com este modelo não serão alteradas.'
              : 'Crie um modelo de receita para reutilização rápida durante o atendimento.'
            }
          </DialogDescription>
        </DialogHeader>

        {/* Aviso de segurança */}
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Receita modelo – revisar antes de prescrever</strong>
            <p className="mt-1 text-amber-700">
              Templates são modelos para agilizar a prescrição. Sempre revise os medicamentos 
              e dosagens antes de salvar uma receita para o paciente.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Nome do Template */}
          <div>
            <Label className="text-sm font-semibold">
              Nome da Receita Modelo *
            </Label>
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Ex: IVAS Adulto, Hipertensão Inicial, Gastrite..."
              className="mt-1 h-11"
              data-testid="template-name-input"
            />
          </div>

          {/* Descrição e Especialidade */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">
                Indicação Clínica / Descrição
              </Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Tratamento inicial para hipertensão estágio 1"
                className="mt-1 h-20"
                data-testid="template-description"
              />
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">
                  Especialidade
                </Label>
                <Input
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  placeholder="Ex: Cardiologia, Clínica Geral..."
                  className="mt-1"
                  data-testid="template-specialty"
                />
              </div>
              
              <div>
                <Label className="text-sm font-semibold">
                  Tags de Doenças (separadas por vírgula)
                </Label>
                <Input
                  value={diseaseTags}
                  onChange={(e) => setDiseaseTags(e.target.value)}
                  placeholder="Ex: hipertensão, HAS, pressão alta"
                  className="mt-1"
                  data-testid="template-tags"
                />
              </div>
            </div>
          </div>

          {/* Favorito */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="isFavorite"
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="isFavorite" className="text-sm font-medium text-gray-700 cursor-pointer">
              ⭐ Marcar como favorito (aparece primeiro na lista)
            </label>
          </div>

          {/* Medicamentos */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Pill className="w-5 h-5 text-primary" />
              <Label className="text-sm font-semibold">Medicamentos *</Label>
            </div>
            
            <MedicationGroupByRoute
              groups={medicationGroups}
              onChange={setMedicationGroups}
              dataTestIdPrefix="template-"
            />
          </div>

          {/* Botões */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-primary"
              data-testid="save-template-btn"
            >
              {loading ? 'Salvando...' : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {template ? 'Atualizar Template' : 'Salvar Template'}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
